import React from 'react';
import { useGetPromoCodesByPromoterIdQuery, useUpdatePromoCodeByIdMutation } from '../promoter.service';
import { Spinner } from '../../../components/shared/atoms/spinner';
// @ts-ignore - sweetalert2 has no types in current setup
import Swal from 'sweetalert2';

interface PromoCodesSectionProps {
  promoterId: string;
  onAddNew: () => void;
}

export const PromoCodesSection = React.forwardRef<
  { refetch: () => void },
  PromoCodesSectionProps
>(({ promoterId, onAddNew }, ref) => {
  const { data: promoCodes = [], isLoading: promoCodesLoading, refetch } = useGetPromoCodesByPromoterIdQuery({ promoterId });
  const [updatePromoCode] = useUpdatePromoCodeByIdMutation();
  
  // Expose refetch function to parent component via ref
  React.useImperativeHandle(ref, () => ({
    refetch
  }), [refetch]);

  const handleEditField = async (promoCodeId: string, field: string, value: any) => {
    try {
      // Show loading
      Swal.fire({
        title: 'Updating...',
        text: `Updating ${field}...`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const updateData: any = {};
      updateData[field] = value;
      
      await updatePromoCode({ promoCodeId, data: updateData }).unwrap();
      
      // Show success
      Swal.fire({
        title: 'Success! 🎉',
        text: `${field} updated successfully`,
        icon: 'success',
        confirmButtonColor: '#3b82f6'
      });
      
      // Refresh the data after successful update
      refetch();
    } catch (error: any) {
      console.error(`Error updating ${field}:`, error);
      
      // Show error
      Swal.fire({
        title: 'Error! ❌',
        text: error?.data?.message || error?.message || `Failed to update ${field}. Please try again.`,
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const handleStatusToggle = async (promoCode: any) => {
    const newStatus = !promoCode.active;
    const statusText = newStatus ? 'activate' : 'deactivate';
    
    const result = await Swal.fire({
      title: `Confirm ${statusText}`,
      text: `Are you sure you want to ${statusText} the promo code "${promoCode.code}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${statusText}`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      await handleEditField(promoCode.id, 'active', newStatus);
    }
  };

  const handleBonusPercentageChange = async (promoCode: any) => {
    const result = await Swal.fire({
      title: 'Update Bonus Percentage',
      html: `
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Bonus Percentage</label>
          <input 
            id="bonusPercentageInput" 
            type="range" 
            min="0" 
            max="100" 
            step="1" 
            value="${promoCode.bonusPercentage}"
            class="w-full h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl appearance-none cursor-pointer"
            style="
              -webkit-appearance: none;
              background: linear-gradient(to right, #3b82f6 0%, #3b82f6 ${promoCode.bonusPercentage}%, #e5e7eb ${promoCode.bonusPercentage}%, #e5e7eb 100%);
            "
          />
          <div class="flex justify-between text-xs text-gray-500 mt-1">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
        </div>
        <div class="text-center">
          <div class="inline-flex items-center px-6 py-3 bg-gradient-to-br from-emerald-100 via-teal-100 to-blue-100 rounded-2xl border-2 border-emerald-300">
            <div class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl mr-3 flex items-center justify-center">
              <svg class="text-white text-sm" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clip-rule="evenodd" />
              </svg>
            </div>
            <div>
              <div class="text-2xl font-black text-emerald-800" id="bonusPercentageDisplay">${promoCode.bonusPercentage}%</div>
              <div class="text-xs text-emerald-600 font-medium">Bonus Percentage</div>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      didOpen: () => {
        const input = document.getElementById('bonusPercentageInput') as HTMLInputElement;
        const display = document.getElementById('bonusPercentageDisplay');
        
        if (input && display) {
          input.addEventListener('input', (e) => {
            const value = (e.target as HTMLInputElement).value;
            display.textContent = `${value}%`;
            
            // Update slider background
            const percentage = (parseInt(value) / 100) * 100;
            input.style.background = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`;
          });
        }
      }
    });

    if (result.isConfirmed) {
      const input = document.getElementById('bonusPercentageInput') as HTMLInputElement;
      if (input) {
        const newPercentage = Number(input.value);
        await handleEditField(promoCode.id, 'bonusPercentage', newPercentage);
      }
    }
  };

  const handleExpiryDateChange = async (promoCode: any) => {
    const currentDate = promoCode.expiryDate.seconds ? new Date(promoCode.expiryDate.seconds * 1000) : new Date();
    
    const result = await Swal.fire({
      title: 'Update Expiry Date',
      html: `
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
          <input 
            id="expiryDateInput" 
            type="date" 
            value="${currentDate.toISOString().split('T')[0]}"
            class="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-500"
            min="${new Date().toISOString().split('T')[0]}"
          />
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      const input = document.getElementById('expiryDateInput') as HTMLInputElement;
      if (input) {
        const newDate = input.value;
        await handleEditField(promoCode.id, 'expiryDate', newDate);
      }
    }
  };

  const handleAppliedToChange = async (promoCode: any) => {
    const currentAppliedTo = promoCode.appliedTo || [];
    
    const result = await Swal.fire({
      title: 'Update Applied To Types',
      html: `
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Select Top-Up Types *</label>
          <div class="space-y-3">
            <div class="flex items-center">
              <input 
                type="checkbox" 
                id="karmaCheckbox" 
                ${currentAppliedTo.includes('KARMA') ? 'checked' : ''}
                class="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2 mr-3"
              />
              <label for="karmaCheckbox" class="text-sm font-medium text-gray-700">KARMA</label>
            </div>
            <div class="flex items-center">
              <input 
                type="checkbox" 
                id="euroCheckbox" 
                ${currentAppliedTo.includes('EUR') ? 'checked' : ''}
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mr-3"
              />
              <label for="euroCheckbox" class="text-sm font-medium text-gray-700">EUR</label>
            </div>
          </div>
          <p class="text-xs text-red-500 mt-2">* At least one type must be selected</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const karmaCheckbox = document.getElementById('karmaCheckbox') as HTMLInputElement;
        const euroCheckbox = document.getElementById('euroCheckbox') as HTMLInputElement;
        
        if (karmaCheckbox && euroCheckbox) {
          const newAppliedTo = [];
          if (karmaCheckbox.checked) newAppliedTo.push('KARMA');
          if (euroCheckbox.checked) newAppliedTo.push('EUR');
          
          if (newAppliedTo.length === 0) {
            Swal.showValidationMessage('Please select at least one top-up type');
            return false;
          }
          
          return newAppliedTo;
        }
        return false;
      }
    });

    if (result.isConfirmed && result.value) {
      await handleEditField(promoCode.id, 'appliedTo', result.value);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPromoCodeStatus = (promoCode: any) => {
    if (!promoCode.active) return 'INACTIVE';
    if (promoCode.expiryDate && new Date(promoCode.expiryDate.seconds * 1000) <= new Date()) return 'EXPIRED';
    return 'ACTIVE';
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'EXPIRED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-3xl shadow-2xl p-8 border border-gray-100 animate-fade-in-up backdrop-blur-sm">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-bold text-gray-900 flex items-center animate-fade-in-left">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-2xl mr-4 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group">
            <svg className="w-7 h-7 text-white group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Promo Codes
            </span>
            <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mt-1"></div>
          </div>
        </h3>
        <button
          className="px-6 py-3 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 hover:from-blue-600 hover:via-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all duration-300 font-semibold flex items-center justify-center shadow-lg hover:shadow-2xl transform hover:-translate-y-1 hover:scale-105 animate-fade-in-right group relative overflow-hidden"
          onClick={onAddNew}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <svg className="w-5 h-5 mr-2 group-hover:rotate-90 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add New
        </button>
      </div>
      
      {/* Promo Codes List */}
      <div className="space-y-4">
        {promoCodesLoading ? (
          <div className="text-center py-12 animate-fade-in">
            <div className="relative">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center animate-bounce">
                <Spinner />
              </div>
              <div className="absolute inset-0 w-16 h-16 mx-auto bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full animate-ping opacity-20"></div>
            </div>
            <p className="text-gray-600 font-medium text-lg">Loading promo codes...</p>
            <p className="text-gray-500 text-sm mt-2">Fetching your promotional campaigns</p>
          </div>
        ) : promoCodes.length === 0 ? (
          <div className="text-center py-12 text-gray-500 animate-fade-in-up">
            <div className="relative mb-6">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <h4 className="text-xl font-bold text-gray-800 mb-2">No promo codes yet</h4>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">Create your first promo code to start promoting and reward your customers</p>
            <button
              className="px-8 py-3 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 hover:from-blue-600 hover:via-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 group relative overflow-hidden"
              onClick={onAddNew}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <svg className="inline-block w-5 h-5 mr-2 group-hover:rotate-90 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Create First Promo Code
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {promoCodes.map((promoCode, index) => {
              const status = getPromoCodeStatus(promoCode);
              return (
                <div 
                  key={index} 
                  className="group bg-gradient-to-br from-white via-white to-blue-50/30 rounded-2xl p-6 border border-gray-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 hover:scale-[1.02] animate-fade-in-up backdrop-blur-sm relative overflow-hidden"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Animated Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Header Section */}
                  <div className="flex items-center justify-between mb-6 relative z-10">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                          <svg className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        {status === 'ACTIVE' && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                        )}
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-300">{promoCode.code}</h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm font-semibold text-blue-600">{promoCode.bonusPercentage}% bonus</span>
                          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Status and Usage Badges */}
                    <div className="flex items-center space-x-3">
                      <div className={`px-4 py-2 text-xs font-bold rounded-xl border-2 ${getStatusBadgeColor(status)} shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105`}>
                        {status}
                      </div>
                      {promoCode.usageLimit && (
                        <div className="flex items-center space-x-2">
                          <div
                            className={
                              `px-4 py-2 text-xs font-bold rounded-xl border-2 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 ` +
                              (
                                promoCode.currentUsage >= promoCode.usageLimit
                                  ? 'bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border-red-200'
                                  : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200'
                              )
                            }
                          >
                            <div className="flex items-center space-x-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                              </svg>
                              <span>Used: {promoCode.currentUsage || 0}/{promoCode.usageLimit}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const newLimit = prompt('Enter new usage limit (leave empty for unlimited):', promoCode.usageLimit?.toString() || '');
                              if (newLimit !== null) {
                                const limit = newLimit.trim() === '' ? null : Number(newLimit);
                                handleEditField(promoCode.id, 'usageLimit', limit);
                              }
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0"
                            title="Edit Usage Limit"
                          >
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Divider with Gradient */}
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-6"></div>
                  
                  {/* Details Grid - Similar to UserDetails layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
                    <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Expires</span>
                          <p className="font-semibold text-gray-900">{formatDate(promoCode.expiryDate)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleExpiryDateChange(promoCode)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0"
                        title="Edit Expiry Date"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Min Purchase</span>
                          <p className="font-semibold text-gray-900">
                            {promoCode.minimumPurchaseAmount ? promoCode.minimumPurchaseAmount : 'No minimum'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const newAmount = prompt('Enter new minimum purchase amount (leave empty for no minimum):', promoCode.minimumPurchaseAmount?.toString() || '');
                          if (newAmount !== null) {
                            const amount = newAmount.trim() === '' ? null : Number(newAmount);
                            handleEditField(promoCode.id, 'minimumPurchaseAmount', amount);
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0"
                        title="Edit Minimum Purchase Amount"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Bonus</span>
                          <p className="font-semibold text-gray-900">{promoCode.bonusPercentage}%</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleBonusPercentageChange(promoCode)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0"
                        title="Edit Bonus Percentage"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    </div>
                    
                                         <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                       <div className="flex items-center space-x-3">
                         <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                           <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                             <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                           </svg>
                         </div>
                         <div>
                           <span className="text-sm font-medium text-gray-500">Applied To</span>
                           <p className="font-semibold text-gray-900">
                             {promoCode.appliedTo && promoCode.appliedTo.length > 0 ? (
                               <div className="flex flex-wrap gap-1">
                                 {promoCode.appliedTo.map((type, index) => (
                                   <span
                                     key={index}
                                     className={`px-2 py-1 text-xs font-medium rounded-lg ${
                                       type === 'KARMA' 
                                         ? 'bg-green-100 text-green-800 border border-green-200' 
                                         : 'bg-blue-100 text-blue-800 border border-blue-200'
                                     }`}
                                   >
                                     {type}
                                   </span>
                                 ))}
                               </div>
                             ) : (
                               <span className="text-gray-500">All types</span>
                             )}
                           </p>
                         </div>
                       </div>
                       <button
                         onClick={() => handleAppliedToChange(promoCode)}
                         className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0"
                         title="Edit Applied To Types"
                       >
                         <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                           <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                         </svg>
                       </button>
                     </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Status</span>
                          <p className="font-semibold text-gray-900">{status}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleStatusToggle(promoCode)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0"
                        title="Toggle Status"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Created</span>
                          <p className="font-semibold text-gray-900">{formatDate(promoCode.createdAt)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Subtle Border Animation */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-blue-200/50 transition-all duration-500"></div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes promoCodeFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes promoCodeFadeInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes promoCodeFadeInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes promoCodeFadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: promoCodeFadeIn 0.6s ease-out;
        }
        
        .animate-fade-in-up {
          animation: promoCodeFadeInUp 0.8s ease-out;
        }
        
        .animate-fade-in-left {
          animation: promoCodeFadeInLeft 0.8s ease-out;
        }
        
        .animate-fade-in-right {
          animation: promoCodeFadeInRight 0.8s ease-out;
        }
      `}</style>
    </div>
  );
});

PromoCodesSection.displayName = 'PromoCodesSection';
